import { router, Stack, useLocalSearchParams } from 'expo-router';
import * as React from 'react';
import { ActivityIndicator, View } from 'react-native';

import { EventForm, type EventEditorCommunity } from '@/components/events/event-form';
import { useSession } from '@/components/session-provider';
import { useTenant } from '@/components/tenant-provider';
import { useCurrentTime } from '@/lib/dev-clock';
import { fetchEventById, type Event } from '@/lib/events';
import { isUserTenantAdmin, memberTenantsForUser } from '@/lib/tenant';
import { Button } from '@sixam/ui/button';
import { Text } from '@sixam/ui/text';
import { isEventEditable } from '@sixam/shared/event-editing-core';

export default function MemberEditEventScreen() {
  const now = useCurrentTime();
  const { id, routeVersionId } = useLocalSearchParams<{ id: string; routeVersionId?: string }>();
  const { isAdmin, isProfileLoading, session } = useSession();
  const { isTenantLoading, memberships, tenants } = useTenant();
  const [event, setEvent] = React.useState<Event | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const communities = React.useMemo<EventEditorCommunity[]>(
    () =>
      memberTenantsForUser(tenants, memberships, isAdmin).map(({ id, name, slug }) => ({
        id,
        name,
        slug,
      })),
    [isAdmin, memberships, tenants]
  );
  const eventCommunity = communities.find((community) => community.id === event?.tenant_id) ?? null;
  const isPersonalEvent = event?.tenant_id === null;
  const userId = session?.user.id;
  const isOperator = isAdmin || isUserTenantAdmin(memberships, event?.tenant_id, session?.user.id);
  const isCreator = Boolean(
    userId && event?.created_by === userId && (isPersonalEvent || eventCommunity)
  );
  const canEdit = isOperator || isCreator;

  const returnToEvent = React.useCallback(() => {
    if (!event) {
      router.back();
      return;
    }
    router.replace({ pathname: '/app/events/[id]', params: { id: event.id } });
  }, [event]);

  React.useEffect(() => {
    let active = true;
    async function load() {
      setIsLoading(true);
      try {
        const nextEvent = await fetchEventById(id);
        if (!active) return;
        if (!nextEvent) throw new Error('Run not found.');
        setEvent(nextEvent);
        setError(null);
      } catch (loadError) {
        if (active) {
          setError(loadError instanceof Error ? loadError.message : 'Could not load this run.');
        }
      } finally {
        if (active) setIsLoading(false);
      }
    }
    void load();
    return () => {
      active = false;
    };
  }, [id]);

  if (isLoading || isProfileLoading || isTenantLoading) {
    return (
      <View className="flex-1 items-center justify-center bg-background">
        <Stack.Screen options={{ headerShown: false }} />
        <ActivityIndicator />
      </View>
    );
  }

  const memberCannotEditPaidRun =
    Boolean(event?.registration_price_thb && event.registration_price_thb > 0) && !isOperator;
  if (
    error ||
    !event ||
    (!eventCommunity && !isPersonalEvent) ||
    !canEdit ||
    memberCannotEditPaidRun
  ) {
    return (
      <View className="flex-1 items-center justify-center gap-4 bg-background p-6">
        <Stack.Screen options={{ headerShown: false }} />
        <Text className="text-center text-destructive">
          {error ??
            (memberCannotEditPaidRun
              ? 'A club admin must edit runs with paid registration.'
              : 'You can only edit runs you created.')}
        </Text>
        <Button onPress={returnToEvent}>
          <Text>Back to run</Text>
        </Button>
      </View>
    );
  }

  if (!isEventEditable(event, now)) {
    return (
      <View className="flex-1 items-center justify-center gap-4 bg-background p-6">
        <Stack.Screen options={{ headerShown: false }} />
        <Text variant="h3" className="text-center">
          Past run
        </Text>
        <Text variant="muted" className="text-center">
          Runs cannot be edited after they end.
        </Text>
        <Button onPress={returnToEvent}>
          <Text>Back to run</Text>
        </Button>
      </View>
    );
  }

  return (
    <EventForm
      event={event}
      initialRouteVersionId={routeVersionId}
      initialSheet={routeVersionId ? 'route' : null}
      managementLevel={isOperator ? 'operator' : 'member'}
      onClose={returnToEvent}
      onSaved={(savedEvent) => {
        router.replace({ pathname: '/app/events/[id]', params: { id: savedEvent.id } });
      }}
      personalEvent={isPersonalEvent}
      tenantId={eventCommunity?.id ?? null}
      tenantName={eventCommunity?.name ?? null}
      tenantSlug={eventCommunity?.slug ?? null}
    />
  );
}
